import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ImpactSection = () => {
  const impactStats = [
    {
      icon: "Heart",
      number: "2.5M+",
      label: "Meaningful Gifts Delivered",
      description: "Gifts that created lasting memories and strengthened relationships"
    },
    {
      icon: "Users",
      number: "500K+",
      label: "Active Community Members",
      description: "Gift-givers sharing experiences and celebrating together"
    },
    {
      icon: "Globe",
      number: "150+",
      label: "Countries Served",
      description: "Honoring diverse cultures and traditions worldwide"
    },
    {
      icon: "Calendar",
      number: "10K+",
      label: "Occasions Celebrated",
      description: "From birthdays to cultural festivals, every moment matters"
    },
    {
      icon: "Award",
      number: "98%",
      label: "Satisfaction Rate",
      description: "Recipients who loved their thoughtfully chosen gifts"
    },
    {
      icon: "Sparkles",
      number: "4.9/5",
      label: "Average Rating",
      description: "Community rating for gift recommendation accuracy"
    }
  ];

  const successStories = [
    {
      title: "Cross-Cultural Connection",
      story: "Maria found the perfect gift for her Japanese colleague's promotion, honoring both personal taste and cultural significance.",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=300&fit=crop",
      impact: "Strengthened workplace relationships"
    },
    {
      title: "Long-Distance Love",
      story: "David surprised his grandmother with a personalized photo album that brought tears of joy across 3,000 miles.",
      image: "https://images.pexels.com/photos/8088495/pexels-photo-8088495.jpeg?w=400&h=300&fit=crop",
      impact: "Bridged generational gaps"
    },
    {
      title: "Community Celebration",
      story: "The Johnson family coordinated 20+ relatives for a surprise anniversary gift that honored 50 years of marriage.",
      image: "https://images.unsplash.com/photo-1511895426328-dc8714191300?w=400&h=300&fit=crop",
      impact: "United extended family"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl lg:text-5xl font-bold text-accent-800 mb-6">
            Our Impact
          </h2>
          <p className="text-lg lg:text-xl text-accent-600 max-w-3xl mx-auto leading-relaxed">
            Every gift tells a story. Here's how Giftsy Modern has helped create meaningful moments and strengthen relationships around the world.
          </p>
        </div>

        {/* Impact Statistics */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {impactStats.map((stat, index) => (
            <div key={index} className="text-center group hover-lift">
              <div className="bg-gradient-warm rounded-2xl p-8 border border-primary-100 h-full transition-all duration-300 group-hover:shadow-lg group-hover:border-primary-200">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Icon name={stat.icon} size={28} className="text-white" />
                </div>
                
                <h3 className="font-heading text-3xl lg:text-4xl font-bold text-accent-800 mb-2">
                  {stat.number}
                </h3>
                
                <h4 className="font-heading text-lg font-bold text-primary-600 mb-3">
                  {stat.label}
                </h4>
                
                <p className="text-accent-600 text-sm leading-relaxed">
                  {stat.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Success Stories */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="font-heading text-2xl lg:text-3xl font-bold text-accent-800 mb-4">
              Success Stories
            </h3>
            <p className="text-lg text-accent-600 max-w-2xl mx-auto">
              Real stories from our community showing how thoughtful gifts create lasting impact
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {successStories.map((story, index) => (
              <div key={index} className="group hover-lift">
                <div className="bg-white rounded-2xl overflow-hidden shadow-md border border-accent-100 h-full transition-all duration-300 group-hover:shadow-lg group-hover:border-primary-200">
                  <div className="relative">
                    <Image
                      src={story.image}
                      alt={story.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>
                  
                  <div className="p-6">
                    <h4 className="font-heading text-xl font-bold text-accent-800 mb-3">
                      {story.title}
                    </h4>
                    
                    <p className="text-accent-600 leading-relaxed mb-4">
                      {story.story}
                    </p>
                    
                    <div className="flex items-center space-x-2 text-primary-600">
                      <Icon name="Heart" size={16} />
                      <span className="text-sm font-medium">{story.impact}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Community Impact */}
        <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-8 lg:p-12 border border-primary-100">
          <div className="text-center mb-8">
            <h3 className="font-heading text-2xl lg:text-3xl font-bold text-accent-800 mb-4">
              Building Stronger Communities
            </h3>
            <p className="text-lg text-accent-600 max-w-2xl mx-auto">
              Beyond individual gifts, we're fostering a culture of thoughtfulness that ripples through communities
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                <Icon name="Users" size={32} className="text-primary-600" />
              </div>
              <h4 className="font-heading text-xl font-bold text-accent-800 mb-2">
                Community Support
              </h4>
              <p className="text-accent-600">
                Members helping each other find perfect gifts through shared experiences and advice
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                <Icon name="Leaf" size={32} className="text-secondary-600" />
              </div>
              <h4 className="font-heading text-xl font-bold text-accent-800 mb-2">
                Sustainable Giving
              </h4>
              <p className="text-accent-600">
                Promoting thoughtful consumption and supporting eco-friendly gift options
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                <Icon name="Globe" size={32} className="text-accent-600" />
              </div>
              <h4 className="font-heading text-xl font-bold text-accent-800 mb-2">
                Cultural Bridge
              </h4>
              <p className="text-accent-600">
                Connecting people across cultures through understanding and respectful gift-giving
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactSection;